import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaUserMd, FaClock, FaCopy, FaProcedures } from 'react-icons/fa';
import { apiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

const LATERALITY_CHOICES = [
    { value: 'left', label: 'Left', badge: 'badge-info' },
    { value: 'right', label: 'Right', badge: 'badge-warning' },
    { value: 'bilateral', label: 'Bilateral', badge: 'badge-success' },
    { value: 'na', label: 'N/A', badge: 'badge-ghost' }
];

const BODY_AREA_CHOICES = [
    { value: '', label: 'Not Set' },
    { value: 'head', label: 'Head' },
    { value: 'face', label: 'Face' },
    { value: 'neck', label: 'Neck' },
    { value: 'shoulder', label: 'Shoulder' },
    { value: 'upper_arm', label: 'Upper Arm' },
    { value: 'elbow', label: 'Elbow' },
    { value: 'forearm', label: 'Forearm' },
    { value: 'wrist', label: 'Wrist' },
    { value: 'hand', label: 'Hand' },
    { value: 'chest', label: 'Chest' },
    { value: 'upper_back', label: 'Upper Back (Thoracic)' },
    { value: 'lower_back', label: 'Lower Back' },
    { value: 'abdomen', label: 'Abdomen' },
    { value: 'pelvis_groin', label: 'Pelvis/Groin' },
    { value: 'buttocks', label: 'Buttocks' },
    { value: 'hip', label: 'Hip' },
    { value: 'thigh', label: 'Thigh' },
    { value: 'knee', label: 'Knee' },
    { value: 'shin', label: 'Shin' },
    { value: 'calf', label: 'Calf' },
    { value: 'ankle', label: 'Ankle' },
    { value: 'foot', label: 'Foot' }
];

interface ProcedureType {
    id: string;
    code?: string;
    name: string;
    description?: string;
    categories: string[];
    category_names?: string[];
    requires_anaesthetist: boolean;
    default_duration_minutes: number;
    estimated_fee?: string;
    provider: string;
    provider_name?: string;
    laterality?: string;
    body_area?: string;
    is_active: boolean;
}

interface Provider {
    id: string;
    first_name: string;
    last_name: string;
    provider_type_display?: string;
    performs_procedures?: boolean;
}

interface Category {
    id: string;
    name: string;
    code?: string;
}

interface CategoryOption {
    value: string;
    label: string;
    code?: string;
}

interface ProcedureFormData {
    code: string;
    name: string;
    description: string;
    categories: string[];
    requires_anaesthetist: boolean;
    default_duration_minutes: number;
    estimated_fee: string;
    provider: string;
    laterality: string;
    body_area: string;
    is_active: boolean;
}

interface DuplicateFormData {
    target_provider_id: string;
    copy_operation_report: boolean;
}

const EMPTY_FORM_DATA: ProcedureFormData = {
    code: '',
    name: '',
    description: '',
    categories: [],
    requires_anaesthetist: false,
    default_duration_minutes: 60,
    estimated_fee: '',
    provider: '',
    laterality: 'na',
    body_area: '',
    is_active: true
};

const ProcedureTypes = () => {
    const { token } = useAuth();
    const [procedureTypes, setProcedureTypes] = useState<ProcedureType[]>([]);
    const [providers, setProviders] = useState<Provider[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [editingProcedure, setEditingProcedure] = useState<ProcedureType | null>(null);
    const [duplicatingProcedure, setDuplicatingProcedure] = useState<ProcedureType | null>(null);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedProvider, setSelectedProvider] = useState('all');
    const [selectedLaterality, setSelectedLaterality] = useState('all');
    const [selectedBodyArea, setSelectedBodyArea] = useState('all');
    const [categories, setCategories] = useState<Category[]>([]);
    const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false); // Add submission guard

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const itemsPerPage = 7;

    const [formData, setFormData] = useState<ProcedureFormData>(EMPTY_FORM_DATA);

    const [duplicateFormData, setDuplicateFormData] = useState<DuplicateFormData>({
        target_provider_id: '',
        copy_operation_report: false
    });

    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadCategories();
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategory, selectedProvider, selectedLaterality, selectedBodyArea, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedCategory, selectedProvider, selectedLaterality, selectedBodyArea, searchTerm]);

    const loadCategories = async () => {
        if (!token) return;
        try {
            const response = await apiCall('/scheduling/procedure-type-categories/', {
                method: 'GET'
            }, token);
            if (response.ok) {
                const data = await response.json();
                const categoriesArray: Category[] = data.results || data;
                setCategories(Array.isArray(categoriesArray) ? categoriesArray : []);
                setCategoryOptions(categoriesArray.map(cat => ({
                    value: cat.id,
                    label: cat.name,
                    code: cat.code
                })));
            }
        } catch (error) {
            console.error('Error loading categories:', error);
            alert('Failed to load categories');
            setCategories([]);
        }
    };

    const loadData = async () => {
        if (!token) return;
        setLoading(true);
        try {
            const filterParams: string[] = [];
            if (selectedCategory !== 'all') {
                filterParams.push(`categories=${selectedCategory}`);
            }
            if (selectedProvider !== 'all') {
                filterParams.push(`provider=${selectedProvider}`);
            }
            if (selectedLaterality !== 'all') {
                filterParams.push(`laterality=${selectedLaterality}`);
            }
            if (selectedBodyArea !== 'all') {
                filterParams.push(`body_area=${selectedBodyArea}`);
            }
            if (searchTerm) {
                filterParams.push(`search=${encodeURIComponent(searchTerm)}`);
            }

            filterParams.push(`page=${currentPage}`);
            filterParams.push(`page_size=${itemsPerPage}`);

            const queryString = filterParams.length > 0 ? `?${filterParams.join('&')}` : '';

            const procedureResponse = await apiCall(`/scheduling/procedure-types/${queryString}`, {
                method: 'GET'
            }, token);

            if (procedureResponse.ok) {
                const procedureData = await procedureResponse.json();

                if (procedureData.results) {
                    setProcedureTypes(procedureData.results);
                    setTotalCount(procedureData.count || 0);
                    setTotalPages(Math.ceil((procedureData.count || 0) / itemsPerPage));
                } else if (Array.isArray(procedureData)) {
                    setProcedureTypes(procedureData);
                    setTotalCount(procedureData.length);
                    setTotalPages(1);
                } else {
                    // Fallback if data is not in expected format
                    setProcedureTypes([]);
                    setTotalCount(0);
                    setTotalPages(1);
                }
            }

            const providerResponse = await apiCall('/providers/providers/', {
                method: 'GET'
            }, token);

            if (providerResponse.ok) {
                const providerData = await providerResponse.json();
                // Filter to only show providers who perform procedures
                const allProviders: Provider[] = providerData.results || providerData;
                const procedureProviders = Array.isArray(allProviders)
                    ? allProviders.filter(provider => provider.performs_procedures === true)
                    : [];
                setProviders(procedureProviders);
            }

        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Prevent double submission
        if (isSubmitting) {
            console.log('Submission already in progress, ignoring duplicate submit');
            return;
        }

        if (formData.categories.length === 0) {
            alert('Please select at least one category');
            return;
        }

        if (!token) return;

        setIsSubmitting(true); // Set guard

        try {
            const endpoint = editingProcedure
                ? `/scheduling/procedure-types/${editingProcedure.id}/`
                : '/scheduling/procedure-types/';

            const method = editingProcedure ? 'PUT' : 'POST';

            const response = await apiCall(endpoint, {
                method,
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            }, token);

            if (response.ok) {
                setShowModal(false);
                resetForm();
                await loadData(); // Wait for data to load
            } else {
                const errorData = await response.json();
                console.error('Error details:', errorData);
                alert(`Failed to save procedure type: ${JSON.stringify(errorData)}`);
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Failed to save procedure type');
        } finally {
            setIsSubmitting(false); // Release guard
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this procedure type?')) return;
        if (!token) return;

        try {
            const response = await apiCall(`/scheduling/procedure-types/${id}/`, {
                method: 'DELETE'
            }, token);

            if (response.ok) {
                loadData();
            } else {
                alert('Failed to delete procedure type');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Failed to delete procedure type');
        }
    };

    const handleEdit = (procedure: ProcedureType) => {
        setEditingProcedure(procedure);
        setFormData({
            code: procedure.code || '',
            name: procedure.name,
            description: procedure.description || '',
            categories: procedure.categories || [],
            requires_anaesthetist: procedure.requires_anaesthetist,
            default_duration_minutes: procedure.default_duration_minutes,
            estimated_fee: procedure.estimated_fee || '',
            provider: procedure.provider,
            laterality: procedure.laterality || 'na',
            body_area: procedure.body_area || '',
            is_active: procedure.is_active
        });
        setShowModal(true);
    };

    const handleDuplicate = (procedure: ProcedureType) => {
        setDuplicatingProcedure(procedure);
        setDuplicateFormData({
            target_provider_id: '',
            copy_operation_report: false
        });
        setShowDuplicateModal(true);
    };

    const handleDuplicateSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!duplicateFormData.target_provider_id) {
            alert('Please select a target provider');
            return;
        }

        if (!token || !duplicatingProcedure) return;
        try {
            const response = await apiCall(`/scheduling/procedure-types/${duplicatingProcedure.id}/duplicate/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(duplicateFormData)
            }, token);

            if (response.ok) {
                setShowDuplicateModal(false);
                setDuplicatingProcedure(null);
                setDuplicateFormData({ target_provider_id: '', copy_operation_report: false });
                loadData();
                alert('Procedure type duplicated successfully!');
            } else {
                const errorData = await response.json();
                alert(`Failed to duplicate: ${JSON.stringify(errorData)}`);
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Failed to duplicate procedure type');
        }
    };

    const resetForm = () => {
        setFormData(EMPTY_FORM_DATA);
        setEditingProcedure(null);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type, checked } = e.target as HTMLInputElement;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const filteredProcedureTypes = (Array.isArray(procedureTypes) ? procedureTypes : []).filter(procedure => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            procedure.code?.toLowerCase().includes(searchLower) ||
            procedure.name?.toLowerCase().includes(searchLower) ||
            procedure.description?.toLowerCase().includes(searchLower) ||
            procedure.provider_name?.toLowerCase().includes(searchLower)
        );
    });

    const getLateralityBadge = (laterality?: string) => {
        const choice = LATERALITY_CHOICES.find(c => c.value === laterality);
        if (!choice) return null;
        return <span className={`badge ${choice.badge} px-3 py-3`}>{choice.label}</span>;
    };

    const getBodyAreaLabel = (bodyArea?: string) => {
        const choice = BODY_AREA_CHOICES.find(c => c.value === bodyArea);
        return choice ? choice.label : '-';
    };

    return (
        <div className="flex flex-col h-[calc(100vh-134px)] p-6 bg-base-100">
            <div className="mb-4 flex justify-between items-center flex-shrink-0">
                <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
                    <FaProcedures className="text-primary" />
                    Procedure Types
                </h1>
                <button
                    onClick={() => {
                        resetForm();
                        setShowModal(true);
                    }}
                    className="btn btn-primary"
                >
                    <FaPlus className="mr-2" />
                    Add Procedure Type
                </button>
            </div>

            {/* Filter Section */}
            <div className="mb-6 space-y-4 flex-shrink-0">
                <div className="flex gap-4 items-center flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                        <input
                            type="text"
                            placeholder="Search procedures..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="input input-bordered w-full"
                        />
                    </div>

                    <div className="w-48">
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="select select-bordered w-full"
                        >
                            <option value="all">All Categories</option>
                            {categories.map(category => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="w-48">
                        <select
                            value={selectedProvider}
                            onChange={(e) => setSelectedProvider(e.target.value)}
                            className="select select-bordered w-full"
                        >
                            <option value="all">All Providers</option>
                            {providers.map(provider => (
                                <option key={provider.id} value={provider.id}>
                                    {provider.first_name} {provider.last_name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="w-40">
                        <select
                            value={selectedLaterality}
                            onChange={(e) => setSelectedLaterality(e.target.value)}
                            className="select select-bordered w-full"
                        >
                            <option value="all">All Sides</option>
                            {LATERALITY_CHOICES.map(choice => (
                                <option key={choice.value} value={choice.value}>
                                    {choice.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="w-56">
                        <select
                            value={selectedBodyArea}
                            onChange={(e) => setSelectedBodyArea(e.target.value)}
                            className="select select-bordered w-full"
                        >
                            <option value="all">All Body Areas</option>
                            {BODY_AREA_CHOICES.filter(c => c.value).map(choice => (
                                <option key={choice.value} value={choice.value}>
                                    {choice.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {!loading && (
                    <div className="text-sm text-slate-600">
                        Showing {procedureTypes.length} of {totalCount} procedure types
                        {searchTerm && ` matching "${searchTerm}"`}
                    </div>
                )}
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto">
                {loading ? (
                    <div className="text-center py-8">
                        <div className="loading loading-spinner loading-lg"></div>
                        <p className="mt-4 text-slate-600">Loading procedure types...</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="table table-zebra w-full">
                            <thead className="sticky top-0 bg-base-200 z-10">
                                <tr>
                                    <th>Code</th>
                                    <th>Procedure Name</th>
                                    <th className="text-center">Side</th>
                                    <th>Body Area</th>
                                    <th>Categories</th>
                                    <th>Duration</th>
                                    <th>Provider</th>
                                    <th className="text-center">Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredProcedureTypes.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="text-center py-8 text-slate-500">
                                            {searchTerm ?
                                                `No procedure types found matching "${searchTerm}"` :
                                                'No procedure types found. Click "Add Procedure Type" to create one.'
                                            }
                                        </td>
                                    </tr>
                                ) : (
                                    filteredProcedureTypes.map(procedure => (
                                        <tr key={procedure.id}>
                                            <td>
                                                <span className="badge badge-outline">{procedure.code || '-'}</span>
                                            </td>
                                            <td>
                                                <div className="font-medium">{procedure.name}</div>
                                                {procedure.description && (
                                                    <div className="text-sm text-slate-500 mt-1">{procedure.description}</div>
                                                )}
                                            </td>
                                            <td className="text-center">{getLateralityBadge(procedure.laterality)}</td>
                                            <td>
                                                <span className="text-sm">{getBodyAreaLabel(procedure.body_area)}</span>
                                            </td>
                                            <td>
                                                <div className="flex flex-wrap gap-1">
                                                    {procedure.category_names && procedure.category_names.length > 0 ? (
                                                        procedure.category_names.map((catName, idx) => {
                                                            // Abbreviate long category names
                                                            let displayName = catName;
                                                            if (catName === 'Radio Frequency Neurotomy') displayName = 'RFN';
                                                            if (catName === 'Nerve Block') displayName = 'Blocks';
                                                            if (catName === 'Injection/Epidural') displayName = 'Inj/Epi';
                                                            if (catName === 'Implant Procedure') displayName = 'Implant';
                                                            return (
                                                                <span key={idx} className="badge badge-outline px-3 py-3">
                                                                    {displayName}
                                                                </span>
                                                            );
                                                        })
                                                    ) : (
                                                        <span className="text-slate-400 text-sm">No categories</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="flex items-center gap-1">
                                                    <FaClock className="text-slate-400" />
                                                    {procedure.default_duration_minutes} min
                                                </div>
                                            </td>
                                            <td>
                                                <div className="flex items-center gap-1">
                                                    <FaUserMd className="text-slate-400" />
                                                    <span className="font-medium">{procedure.provider_name}</span>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                {procedure.is_active ? (
                                                    <span className="badge badge-success px-3 py-3">Active</span>
                                                ) : (
                                                    <span className="badge badge-ghost px-3 py-3">Inactive</span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="flex gap-1">
                                                    <button
                                                        onClick={() => handleEdit(procedure)}
                                                        className="btn btn-sm btn-ghost text-primary"
                                                        title="Edit"
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDuplicate(procedure)}
                                                        className="btn btn-sm btn-ghost text-info"
                                                        title="Duplicate"
                                                    >
                                                        <FaCopy />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(procedure.id)}
                                                        className="btn btn-sm btn-ghost text-error"
                                                        title="Delete"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {!loading && totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-6 pb-4 flex-shrink-0">
                    <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        className="btn btn-sm"
                    >
                        First
                    </button>
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="btn btn-sm"
                    >
                        Previous
                    </button>

                    <span className="px-4">
                        Page {currentPage} of {totalPages}
                    </span>

                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="btn btn-sm"
                    >
                        Next
                    </button>
                    <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        className="btn btn-sm"
                    >
                        Last
                    </button>
                </div>
            )}

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-2xl">
                        <h3 className="font-bold text-lg mb-4">
                            {editingProcedure ? 'Edit Procedure Type' : 'Add Procedure Type'}
                        </h3>
                        <form onSubmit={handleSubmit}>
                            <div className="form-control mb-4">
                                <label className="label">
                                    <span className="label-text">Code *</span>
                                </label>
                                <input
                                    type="text"
                                    name="code"
                                    value={formData.code}
                                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                                    className="input input-bordered"
                                    placeholder="e.g., NC, NMC, NC-TELE"
                                    required
                                />
                            </div>

                            <div className="form-control mb-4">
                                <label className="label">
                                    <span className="label-text">Procedure Name *</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="e.g., Cervical C5/6/7 Pulsed Medial Branch RFN"
                                    required
                                />
                            </div>

                            <div className="form-control mb-4">
                                <label className="label">
                                    <span className="label-text">Description</span>
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    className="textarea textarea-bordered"
                                    rows={3}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Laterality *</span>
                                    </label>
                                    <select
                                        name="laterality"
                                        value={formData.laterality}
                                        onChange={handleInputChange}
                                        className="select select-bordered"
                                        required
                                    >
                                        {LATERALITY_CHOICES.map(choice => (
                                            <option key={choice.value} value={choice.value}>
                                                {choice.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Body Area</span>
                                    </label>
                                    <select
                                        name="body_area"
                                        value={formData.body_area}
                                        onChange={handleInputChange}
                                        className="select select-bordered"
                                    >
                                        {BODY_AREA_CHOICES.map(choice => (
                                            <option key={choice.value} value={choice.value}>
                                                {choice.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-control mb-4">
                                <label className="label">
                                    <span className="label-text">Categories * (select one or more)</span>
                                </label>
                                <div className="flex flex-wrap gap-2 p-3 border border-base-300 rounded-lg bg-base-100 min-h-[60px]">
                                    {categoryOptions.map(option => (
                                        <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-sm checkbox-primary"
                                                checked={formData.categories.includes(option.value)}
                                                onChange={(e) => {
                                                    const newCategories = e.target.checked
                                                        ? [...formData.categories, option.value]
                                                        : formData.categories.filter(id => id !== option.value);
                                                    setFormData({ ...formData, categories: newCategories });
                                                }}
                                            />
                                            <span className="text-sm">{option.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Duration (minutes)</span>
                                    </label>
                                    <input
                                        type="number"
                                        name="default_duration_minutes"
                                        value={formData.default_duration_minutes}
                                        onChange={handleInputChange}
                                        className="input input-bordered"
                                        min="0"
                                    />
                                </div>

                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Estimated Fee ($)</span>
                                    </label>
                                    <input
                                        type="number"
                                        name="estimated_fee"
                                        value={formData.estimated_fee}
                                        onChange={handleInputChange}
                                        className="input input-bordered"
                                        step="0.01"
                                        min="0"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div className="form-control">
                                    <label className="label cursor-pointer">
                                        <span className="label-text">Requires Anaesthetist</span>
                                        <input
                                            type="checkbox"
                                            name="requires_anaesthetist"
                                            checked={formData.requires_anaesthetist}
                                            onChange={handleInputChange}
                                            className="checkbox checkbox-primary"
                                        />
                                    </label>
                                </div>

                                <div className="form-control">
                                    <label className="label cursor-pointer">
                                        <span className="label-text">Active</span>
                                        <input
                                            type="checkbox"
                                            name="is_active"
                                            checked={formData.is_active}
                                            onChange={handleInputChange}
                                            className="checkbox checkbox-primary"
                                        />
                                    </label>
                                </div>
                            </div>

                            <div className="form-control mb-4">
                                <label className="label">
                                    <span className="label-text">Provider *</span>
                                </label>
                                <select
                                    name="provider"
                                    value={formData.provider}
                                    onChange={handleInputChange}
                                    className="select select-bordered"
                                    required
                                >
                                    <option value="">Select a provider</option>
                                    {providers.map(provider => (
                                        <option key={provider.id} value={provider.id}>
                                            {provider.first_name} {provider.last_name} - {provider.provider_type_display}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="modal-action">
                                <button type="button" className="btn" onClick={() => { setShowModal(false); resetForm(); }} disabled={isSubmitting}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                    {isSubmitting ? 'Saving...' : (editingProcedure ? 'Update' : 'Create')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Duplicate Modal */}
            {showDuplicateModal && duplicatingProcedure && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg mb-4">
                            Duplicate Procedure Type
                        </h3>
                        <p className="mb-4">
                            Duplicating: <strong>{duplicatingProcedure.name}</strong>
                        </p>
                        <form onSubmit={handleDuplicateSubmit}>
                            <div className="form-control mb-4">
                                <label className="label">
                                    <span className="label-text">Target Provider *</span>
                                </label>
                                <select
                                    value={duplicateFormData.target_provider_id}
                                    onChange={(e) => setDuplicateFormData({ ...duplicateFormData, target_provider_id: e.target.value })}
                                    className="select select-bordered"
                                    required
                                >
                                    <option value="">Select a provider</option>
                                    {providers.filter(p => p.id !== duplicatingProcedure.provider).map(provider => (
                                        <option key={provider.id} value={provider.id}>
                                            {provider.first_name} {provider.last_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control mb-4">
                                <label className="label cursor-pointer">
                                    <span className="label-text">Copy Operation Report Template</span>
                                    <input
                                        type="checkbox"
                                        checked={duplicateFormData.copy_operation_report}
                                        onChange={(e) => setDuplicateFormData({ ...duplicateFormData, copy_operation_report: e.target.checked })}
                                        className="checkbox checkbox-primary"
                                    />
                                </label>
                            </div>

                            <div className="modal-action">
                                <button type="button" className="btn" onClick={() => { setShowDuplicateModal(false); setDuplicatingProcedure(null); }}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Duplicate
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProcedureTypes;
